import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  TrendingUp,
  Trophy,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Camera,
} from "lucide-react";
import { useSeason } from "../context/SeasonContext";
import { supabase } from "../supabaseClient";
import { useStandings } from "../hooks/useStandings";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";

// ─────────────────────────────────────────────────────────────────────────────
// Date helper — parse Supabase datetime strings as local time, not UTC
// ─────────────────────────────────────────────────────────────────────────────
const localDate = (str) => {
  if (!str) return new Date(NaN);
  const [datePart, timePart = "00:00:00"] = str.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  return new Date(year, month - 1, day, hour, minute);
};

// ─────────────────────────────────────────────────────────────────────────────
// Team color helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Resolve a team object (or partial color fields) into a full style set */
const resolveTeamStyle = (team) => {
  if (!team) return getTeamStyle({});
  return getTeamStyle({
    primary_color: team.primary_color,
    secondary_color: team.secondary_color,
    gradient: team.gradient,
  });
};

/**
 * A small colored dot that represents a team's primary color.
 */
// TeamColorDot kept for potential future use — currently unused after QuickLinks redesign
// const TeamColorDot = ({ team, size = 8 }) => { ... };

/**
 * A colored left-border accent bar for list rows.
 */
const TeamAccentBar = ({ team, className = "" }) => {
  const ts = resolveTeamStyle(team);
  return (
    <div
      className={`w-1 self-stretch rounded-full flex-shrink-0 min-h-[28px] ${className}`}
      style={ts.accentStyle}
    />
  );
};

/**
 * Compute relative luminance of a hex color (WCAG formula, same as polished.getLuminance).
 * Returns a value in [0, 1] where 0 = black, 1 = white.
 */
const hexLuminance = (hex) => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const toLinear = (c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * JerseyAvatar — sleeveless basketball jersey SVG with:
 *  • V-neck collar with layered depth strokes
 *  • Luminance-based number contrast (#fff when dark bg, #000 when light bg)
 *  • Soft indigo drop-shadow glow wrapper
 */
const JerseyAvatar = ({ team, number, size = 44 }) => {
  const ts = resolveTeamStyle(team);
  const primaryHex = ts.primaryHex;
  const secondaryHex = ts.accentHex;

  // Luminance-based text contrast (threshold 0.35 per spec)
  const lum = hexLuminance(primaryHex);
  const numberColor = lum < 0.35 ? "#FFFFFF" : "#000000";

  // Slightly lighter/darker inner highlight for collar depth
  const collarHighlight =
    lum < 0.35 ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)";

  return (
    <div
      className="flex-shrink-0"
      style={{
        width: size,
        height: size,
        filter: `drop-shadow(0 2px 5px rgba(0,0,0,0.5)) drop-shadow(0 0 4px rgba(99,102,241,0.2))`,
      }}
      aria-hidden="true"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 44 50"
        fill="none"
        xmlns="http://w3.org"
      >
        <defs>
          {/* Geometric, scalable representation of the traditional Kuffiyeh fishnet pattern */}
          <pattern
            id="kuffiyehPattern"
            width="4"
            height="4"
            patternUnits="userSpaceOnUse"
          >
            {/* Sharp grid network backing */}
            <path
              d="M 0 2 L 4 2 M 2 0 L 2 4"
              stroke="#000000"
              strokeWidth="0.4"
              opacity="0.8"
            />
            {/* Contrasting interlocking diamond points */}
            <path
              d="M 0 0 L 2 2 L 4 0 M 0 4 L 2 2 L 4 4"
              stroke="#000000"
              strokeWidth="0.5"
              fill="none"
            />
            <path d="M 0 2 L 2 0 L 4 2 L 2 4 Z" fill="#FFFFFF" opacity="0.15" />
          </pattern>

          {/* A clip-path matching the exact main silhouette so the patterns don't bleed out */}
          <clipPath id="jerseyClip">
            <path
              d="
        M 12 4 L 16 4 Q 22 14 28 4 L 32 4
        Q 30 12 38 20 L 38 45 Q 38 48 35 48
        L 9 48 Q 6 48 6 45 L 6 20 Q 14 12 12 4 Z
      "
            />
          </clipPath>
        </defs>

        {/* Base Jersey Body Silhouette */}
        <path
          d="
      M 12 4 L 16 4 Q 22 14 28 4 L 32 4
      Q 30 12 38 20 L 38 45 Q 38 48 35 48
      L 9 48 Q 6 48 6 45 L 6 20 Q 14 12 12 4 Z
    "
          fill={primaryHex}
          stroke={secondaryHex}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Decorative Side Panels inside the Clip Path */}
        <g clipPath="url(#jerseyClip)">
          {/* Left Side Panel Strip (Solid White Backdrop + Pattern Overlay) */}
          <path d="M 6 20 L 11 20 L 11 48 L 6 48 Z" fill="#FFFFFF" />
          <path
            d="M 6 20 L 11 20 L 11 48 L 6 48 Z"
            fill="url(#kuffiyehPattern)"
          />
          <line
            x1="11"
            y1="20"
            x2="11"
            y2="48"
            stroke="#000000"
            strokeWidth="0.8"
          />

          {/* Right Side Panel Strip (Solid White Backdrop + Pattern Overlay) */}
          <path d="M 33 20 L 38 20 L 38 48 L 33 48 Z" fill="#FFFFFF" />
          <path
            d="M 33 20 L 38 20 L 38 48 L 33 48 Z"
            fill="url(#kuffiyehPattern)"
          />
          <line
            x1="33"
            y1="20"
            x2="33"
            y2="48"
            stroke="#000000"
            strokeWidth="0.8"
          />
        </g>

        {/* Accent Piping: Left Armhole Trim */}
        <path
          d="M 12 4 Q 14 12 6 20"
          fill="none"
          stroke={secondaryHex}
          strokeWidth="1.5"
          strokeLinecap="round"
        />

        {/* Accent Piping: Right Armhole Trim */}
        <path
          d="M 32 4 Q 30 12 38 20"
          fill="none"
          stroke={secondaryHex}
          strokeWidth="1.5"
          strokeLinecap="round"
        />

        {/* V-Neck Collar Trim */}
        <path
          d="M 16 4 Q 22 14 28 4"
          fill="none"
          stroke={secondaryHex}
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* V-Neck Inner Highlight for Depth */}
        <path
          d="M 16.5 5.2 Q 22 14.5 27.5 5.2"
          fill="none"
          stroke={collarHighlight}
          strokeWidth="1"
          strokeLinecap="round"
        />

        {/* Jersey Number */}
        {number != null && number !== "" && (
          <text
            x="22"
            y="32"
            textAnchor="middle"
            dominantBaseline="central"
            fill={numberColor}
            fontFamily="'Oswald', sans-serif"
            fontWeight="900"
            fontSize={String(number).length > 1 ? "13" : "16"}
          >
            {number}
          </text>
        )}
      </svg>
    </div>
  );
};

/**
 * TeamLogoOrInitials — shows logo if available, otherwise a colored initials badge.
 */
const TeamLogoOrInitials = ({ team, size = 32, className = "" }) => {
  const ts = resolveTeamStyle(team);
  const isDark = isDarkModeActive();
  const textColor = getTextColorForAccent(ts.primaryHex, isDark);
  const initials = (team?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  if (team?.logo_url) {
    return (
      <img
        src={team.logo_url}
        alt={team.name}
        className={`rounded-full object-cover flex-shrink-0 ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className={`rounded-full flex items-center justify-center flex-shrink-0 font-black text-xs ${className}`}
      style={{ width: size, height: size, ...ts.accentStyle, color: textColor }}
    >
      {initials}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Small shared components
// ─────────────────────────────────────────────────────────────────────────────

const SectionHeader = ({ icon: Icon, title, linkLabel, onLink }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">
      <Icon size={14} className="text-orange-500" />
      {title}
    </h2>
    {onLink && (
      <button
        onClick={onLink}
        className="text-xs text-orange-500 hover:text-orange-600 dark:hover:text-orange-400 transition-colors font-medium"
      >
        {linkLabel} →
      </button>
    )}
  </div>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden ${className}`}
  >
    {children}
  </div>
);

const SponsorCard = ({ sponsor }) => (
  <a
    href={sponsor.website_url || "#"}
    target={sponsor.website_url ? "_blank" : undefined}
    rel="noreferrer"
    title={sponsor.name}
    className="group flex flex-col items-center justify-center gap-3 p-5
               rounded-2xl border border-gray-100 dark:border-gray-700
               bg-white dark:bg-gray-800/60
               hover:border-orange-300 dark:hover:border-orange-600
               hover:shadow-md transition-all duration-200"
  >
    {sponsor.logo_url ? (
      <img
        src={sponsor.logo_url}
        alt={sponsor.name}
        className="h-14 max-w-[120px] w-full object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-200"
      />
    ) : (
      <div className="w-14 h-14 rounded-full flex items-center justify-center bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xl font-black tracking-tight select-none">
        {sponsor.name.slice(0, 2).toUpperCase()}
      </div>
    )}
    <span className="text-xs font-semibold text-center text-gray-500 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors leading-tight max-w-[100px] truncate">
      {sponsor.name}
    </span>
  </a>
);

// ─────────────────────────────────────────────────────────────────────────────
// Live banner
// ─────────────────────────────────────────────────────────────────────────────

const LiveBanner = ({ onNavigate }) => (
  <button
    onClick={onNavigate}
    className="w-full flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-3 transition-colors group"
  >
    <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-200 opacity-75" />
      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
    </span>
    <span className="font-semibold text-sm">Games in progress</span>
    <span className="text-red-200 text-sm hidden sm:block">
      Tap to watch live scores
    </span>
    <span className="ml-auto text-red-200 group-hover:text-white transition-colors text-sm">
      Live Now →
    </span>
  </button>
);

const LazyPhoto = ({ photo, isActive, isPrevious }) => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const src = photo.full_url || photo.thumbnail_url;

  return (
    <>
      {isActive && !hasLoaded && (
        <div className="absolute inset-0 w-full h-full animate-pulse-slow bg-white/10 z-0" />
      )}
      <img
        src={src}
        alt=""
        referrerPolicy="no-referrer"
        onLoad={() => setHasLoaded(true)}
        className={`photo-transition absolute inset-0 w-full h-full object-cover object-center
                    ${isActive && hasLoaded ? "opacity-100 z-10" : "opacity-0 z-0"}
                    ${isPrevious ? "opacity-100 z-0" : ""}`}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Photo Carousel
// ─────────────────────────────────────────────────────────────────────────────

const PhotoCarousel = ({ seasonId }) => {
  const [photos, setPhotos] = useState([]);
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const [fallbackSeasonName, setFallbackSeasonName] = useState(null);

  useEffect(() => {
    if (!seasonId) return;
    const load = async () => {
      // 1. Try the most recent album from the current (active) season
      const { data: albums } = await supabase
        .from("photo_albums")
        .select("id")
        .eq("season_id", seasonId)
        .order("week_number", { ascending: false })
        .limit(1);

      if (albums?.length) {
        const { data: photoRows, error } = await supabase.rpc(
          "get_random_photos_by_album",
          { target_album_id: albums[0].id },
        );
        if (!error && photoRows?.length) {
          setPhotos(photoRows);
          setFallbackSeasonName(null);
          return;
        }
        if (error) console.error("Error fetching photos:", error);
      }

      // 2. No photos in the current season — fall back to the most recent
      //    completed season that has at least one album with photos.
      const { data: prevSeasons } = await supabase
        .from("seasons")
        .select("id, name")
        .eq("status", "completed")
        .order("start_date", { ascending: false });

      if (!prevSeasons?.length) return;

      for (const prev of prevSeasons) {
        const { data: prevAlbums } = await supabase
          .from("photo_albums")
          .select("id")
          .eq("season_id", prev.id)
          .order("week_number", { ascending: false })
          .limit(1);

        if (!prevAlbums?.length) continue;

        const { data: prevPhotos, error: prevErr } = await supabase.rpc(
          "get_random_photos_by_album",
          { target_album_id: prevAlbums[0].id },
        );

        if (!prevErr && prevPhotos?.length) {
          setPhotos(prevPhotos);
          setFallbackSeasonName(prev.name);
          return;
        }
      }
    };
    load();
  }, [seasonId]);

  useEffect(() => {
    if (photos.length < 2) return;
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrent((c) => (c + 1) % photos.length);
        setTimeout(() => setFading(false), 50);
      }, 800);
    }, 4000);
    return () => clearInterval(interval);
  }, [photos.length]);

  if (!photos.length) return <div className="absolute inset-0 z-0" />;

  const prevIndex = (current - 1 + photos.length) % photos.length;

  return (
    <div className="absolute inset-0 overflow-hidden z-0">
      {photos.map((photo, i) => (
        <LazyPhoto
          key={photo.id}
          photo={photo}
          isActive={i === current}
          isPrevious={i === prevIndex}
          isFading={fading}
        />
      ))}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
        {photos.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
              i === current ? "bg-white w-3" : "bg-white/40"
            }`}
          />
        ))}
      </div>
      {fallbackSeasonName && (
        <div className="absolute top-3 left-3 z-20 flex items-center gap-1 bg-black/40 text-white/60 text-[10px] font-medium px-2 py-1 rounded-full backdrop-blur-sm pointer-events-none">
          <Camera className="w-3.5 h-3.5" strokeWidth={2.5} />
          {fallbackSeasonName} highlights
        </div>
      )}
      <a
        href="/gallery"
        onClick={(e) => e.stopPropagation()}
        className="absolute top-3 right-3 z-20 flex items-center gap-1.5 bg-black/40 hover:bg-black/60 text-white/80 hover:text-white text-[11px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm transition"
      >
        <Camera className="w-3.5 h-3.5" strokeWidth={2.5} />
        Gallery
      </a>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Hero — photo carousel background, overlay scrim, title, two CTAs
// ─────────────────────────────────────────────────────────────────────────────

const Hero = ({ activeSeason, onStandings, onStats }) => {
  const hasPhotos = !!activeSeason;

  return (
    <div className="relative rounded-2xl overflow-hidden min-h-[280px] sm:min-h-[340px] flex flex-col justify-end">
      {/* Gradient base */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-500 via-orange-600 to-red-700 dark:from-orange-700 dark:via-orange-800 dark:to-red-900" />

      {/* Rolling photo carousel */}
      {hasPhotos && <PhotoCarousel seasonId={activeSeason.id} />}

      {/* Dark scrim */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

      {/* Decorative basketball court arc lines */}
      <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden opacity-[0.08]">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-52 h-26 border-t-2 border-l-2 border-r-2 border-white rounded-t-full" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-white" />
      </div>

      {/* Content */}
      <div className="relative z-10 p-6 sm:p-8">
        <p className="text-[11px] font-black uppercase tracking-[0.2em] text-orange-300 mb-1">
          {activeSeason?.name
            ? `${activeSeason.name} · In Progress`
            : "Welcome to Muqalab Hoops"}
        </p>
        <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-1 drop-shadow-lg">
          Muqalab Hoops
        </h1>
        <p className="text-white/70 text-sm mb-6 drop-shadow">
          {activeSeason?.name ? `${activeSeason.name} · ` : ""}
          Cleveland's Muslim Basketball League · Sunday Nights
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={onStandings}
            className="bg-white text-orange-600 font-bold px-6 py-2.5 rounded-lg text-sm hover:bg-orange-50 transition shadow-md"
          >
            View Standings
          </button>
          <button
            onClick={onStats}
            className="bg-white/15 hover:bg-white/25 border border-white/30 backdrop-blur-sm text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition"
          >
            View Stats
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Quick-access cards — dynamic team color from top-standing team
// ─────────────────────────────────────────────────────────────────────────────

const QuickLinks = ({ standingsTop, topPlayer, onNavigate }) => {
  // Derive accent from #1 team
  const topTeamStyle = standingsTop ? resolveTeamStyle(standingsTop) : null;

  // Derive accent from top scorer's team — use direct field names from aggregated query
  const topPlayerTeam = topPlayer
    ? {
        primary_color: topPlayer.primary_color,
        secondary_color: topPlayer.secondary_color,
        gradient: topPlayer.gradient,
      }
    : null;
  const topPlayerStyle = topPlayerTeam ? resolveTeamStyle(topPlayerTeam) : null;
  const topPlayerAccent = topPlayerStyle?.primaryHex ?? "#f97316";

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* Standings card — team logo as icon */}
      <button
        onClick={() => onNavigate("/standings")}
        className="relative overflow-hidden rounded-xl p-4 text-left hover:shadow-md transition-all group border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800"
      >
        {/* Color accent strip at top */}
        <div
          className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
          style={{ background: topTeamStyle?.secondaryHex ?? "#f97316" }}
        />
        <div className="flex items-start gap-2 mt-1">
          {/* Team logo / initials badge instead of emoji */}
          <TeamLogoOrInitials team={standingsTop} size={32} />
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-wider text-gray-500 dark:text-gray-200">
              Standings
            </div>
            <div
              className="text-sm font-bold truncate"
              style={{ color: topTeamStyle?.secondaryHex ?? "#f97316" }}
            >
              {standingsTop ? standingsTop.name : "—"}
            </div>
            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
              {standingsTop
                ? `${standingsTop.wins ?? 0}W – ${standingsTop.losses ?? 0}L`
                : "View full table"}
            </div>
          </div>
        </div>
      </button>

      {/* Top Scorer card — jersey avatar as icon */}
      <button
        onClick={() => onNavigate("/stats")}
        className="relative overflow-hidden rounded-xl p-4 text-left hover:shadow-md transition-all group border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800"
      >
        {/* Color accent strip at top */}
        <div
          className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
          style={{ background: topPlayerAccent }}
        />
        <div className="flex items-start gap-2 mt-1">
          {/* Jersey avatar with player's jersey number + team colors */}
          <JerseyAvatar
            team={topPlayerTeam}
            number={topPlayer?.jersey_number}
            size={32}
          />
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-wider text-gray-500 dark:text-gray-200">
              Top Scorer
            </div>
            <div
              className="text-sm font-bold truncate"
              style={{ color: topPlayerAccent }}
            >
              {topPlayer ? topPlayer.player_name : "—"}
            </div>
            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
              {topPlayer
                ? `${topPlayer.ppg} PPG · ${topPlayer.team_name}`
                : "View all stats"}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Recent Results — team color chips for winner/loser rows
// ─────────────────────────────────────────────────────────────────────────────

const RecentResults = ({ games, loading, onGameClick, onSeeAll }) => (
  <Card>
    <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700/60">
      <SectionHeader
        icon={Trophy}
        title="Recent Results"
        linkLabel="All results"
        onLink={onSeeAll}
      />
    </div>
    {loading ? (
      <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center animate-pulse">
        Loading...
      </div>
    ) : games.length === 0 ? (
      <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
        No completed games yet.
      </div>
    ) : (
      <div className="divide-y divide-gray-50 dark:divide-gray-700/40">
        {games.map((game) => {
          const team1Won = game.team1_score >= game.team2_score;
          const winner = team1Won ? game.team1 : game.team2;
          const loser = team1Won ? game.team2 : game.team1;
          const winScore = Math.max(game.team1_score, game.team2_score);
          const loseScore = Math.min(game.team1_score, game.team2_score);
          const winnerStyle = resolveTeamStyle(winner);
          const loserStyle = resolveTeamStyle(loser);

          return (
            <button
              key={game.id}
              onClick={() => onGameClick(game.id)}
              className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group text-left"
            >
              {/* Date */}
              <span className="text-xs text-gray-400 dark:text-gray-500 w-12 flex-shrink-0">
                {localDate(game.game_date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </span>

              {/* Matchup */}
              <div className="flex-1 min-w-0 space-y-1">
                {/* Winner row */}
                <div className="flex items-center gap-2">
                  <div
                    className="w-0.5 h-4 rounded-full flex-shrink-0"
                    style={winnerStyle.accentStyle}
                  />
                  <TeamLogoOrInitials team={winner} size={18} />
                  <span className="text-sm font-bold text-gray-900 dark:text-white truncate flex-1">
                    {winner?.name}
                  </span>
                  <span className="text-sm font-black text-gray-900 dark:text-white tabular-nums flex-shrink-0">
                    {winScore}
                  </span>
                </div>
                {/* Loser row */}
                <div className="flex items-center gap-2">
                  <div
                    className="w-0.5 h-4 rounded-full flex-shrink-0 opacity-30"
                    style={loserStyle.accentStyle}
                  />
                  <TeamLogoOrInitials team={loser} size={18} />
                  <span className="text-xs text-gray-400 dark:text-gray-500 truncate flex-1">
                    {loser?.name}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums flex-shrink-0">
                    {loseScore}
                  </span>
                </div>
              </div>

              <span className="text-gray-300 dark:text-gray-600 group-hover:text-orange-400 transition-colors text-xs ml-1">
                →
              </span>
            </button>
          );
        })}
      </div>
    )}
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
// Standings Snapshot — team color accent bars + colored rank indicators
// ─────────────────────────────────────────────────────────────────────────────

const StandingsSnapshot = ({ teams, loading, onTeamClick, onSeeAll }) => {
  const isDark = isDarkModeActive();

  return (
    <Card>
      <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700/60">
        <SectionHeader
          icon={Trophy}
          title="Standings"
          linkLabel="Full standings"
          onLink={onSeeAll}
        />
      </div>
      {loading ? (
        <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center animate-pulse">
          Loading...
        </div>
      ) : teams.length === 0 ? (
        <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
          No standings yet.
        </div>
      ) : (
        <div className="divide-y divide-gray-50 dark:divide-gray-700/40">
          {teams.map((team, idx) => {
            // Merge team_profiles colors
            const profile =
              team?.team_profiles?.[0] || team?.team_profile || {};
            const teamWithColors = {
              ...team,
              primary_color: profile.primary_color || team.primary_color,
              secondary_color: profile.secondary_color || team.secondary_color,
              gradient: profile.gradient ?? team.gradient ?? false,
            };
            const ts = resolveTeamStyle(teamWithColors);
            const rankTextColor = getTextColorForAccent(ts.primaryHex, isDark);

            return (
              <button
                key={team.id}
                onClick={() => onTeamClick(team.id)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group"
              >
                {/* Rank badge — colored for #1, plain for rest */}
                {idx === 0 ? (
                  <span
                    className="text-xs font-black w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: ts.primaryHex, color: rankTextColor }}
                  >
                    1
                  </span>
                ) : (
                  <span className="text-xs text-gray-400 dark:text-gray-500 w-5 text-center flex-shrink-0">
                    {idx + 1}
                  </span>
                )}

                {/* Team color accent bar */}
                <TeamAccentBar team={teamWithColors} />

                {/* Logo or initials */}
                <TeamLogoOrInitials team={teamWithColors} size={28} />

                {/* Name */}
                <span className="flex-1 text-sm font-medium text-gray-800 dark:text-white text-left truncate">
                  {team.name}
                </span>

                {/* Record */}
                <span className="text-xs font-bold tabular-nums text-gray-500 dark:text-gray-400">
                  {team.wins ?? 0}–{team.losses ?? 0}
                </span>

                <span className="text-gray-300 dark:text-gray-600 group-hover:text-orange-400 transition-colors text-xs">
                  →
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Upcoming Games — team color matchup pills
// ─────────────────────────────────────────────────────────────────────────────

const UpcomingGames = ({ games, loading, onSeeAll }) => (
  <Card>
    <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700/60">
      <SectionHeader
        icon={Calendar}
        title="Upcoming Games"
        linkLabel="Full schedule"
        onLink={onSeeAll}
      />
    </div>
    {loading ? (
      <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center animate-pulse">
        Loading...
      </div>
    ) : games.length === 0 ? (
      <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
        No games scheduled yet.
      </div>
    ) : (
      <div className="divide-y divide-gray-50 dark:divide-gray-700/40">
        {games.map((game) => {
          const d = localDate(game.game_date);
          return (
            <div key={game.id} className="flex items-center gap-3 px-5 py-3">
              <div className="flex-shrink-0 text-center w-10">
                <div className="text-xs font-bold text-orange-500">
                  {d.toLocaleDateString("en-US", { month: "short" })}
                </div>
                <div className="text-sm font-black text-gray-900 dark:text-white leading-tight">
                  {d.getDate()}
                </div>
              </div>
              <div className="w-px h-8 bg-gray-100 dark:bg-gray-700 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                  <TeamLogoOrInitials team={game.team1} size={18} />
                  <span className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                    {game.team1?.name}
                  </span>
                  <span className="text-gray-300 dark:text-gray-600 text-xs font-bold mx-0.5">
                    vs
                  </span>
                  <TeamLogoOrInitials team={game.team2} size={18} />
                  <span className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                    {game.team2?.name}
                  </span>
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {d.toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                  {game.court && ` · ${game.court}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
// League Leaders — jersey avatar + team color accent per player row
// ─────────────────────────────────────────────────────────────────────────────

const LeagueLeaders = ({ leaders, loading, onSeeAll }) => (
  <Card>
    <div className="px-5 py-4 border-b border-gray-50 dark:border-gray-700/60">
      <SectionHeader
        icon={TrendingUp}
        title="League Leaders"
        linkLabel="All stats"
        onLink={onSeeAll}
      />
    </div>
    {loading ? (
      <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center animate-pulse">
        Loading...
      </div>
    ) : leaders.length === 0 ? (
      <div className="px-5 py-6 text-sm text-gray-400 dark:text-gray-500 text-center">
        No stats yet.
      </div>
    ) : (
      <div className="divide-y divide-gray-50 dark:divide-gray-700/40">
        {leaders.map((player, idx) => {
          // Fields now come directly from the aggregated query
          const playerTeam = {
            name: player.team_name,
            logo_url: player.logo_url,
            primary_color: player.primary_color,
            secondary_color: player.secondary_color,
            gradient: player.gradient,
          };
          const ts = resolveTeamStyle(playerTeam);
          const teamNameColor = ts.primaryHex;
          const isFirst = idx === 0;
          return (
            <div
              key={player.player_id}
              className="flex items-center gap-3 px-4 py-3"
            >
              {/* Rank */}
              <span
                className={`text-xs font-bold w-4 text-center flex-shrink-0 ${
                  isFirst
                    ? "text-orange-500"
                    : "text-gray-400 dark:text-gray-500"
                }`}
              >
                {idx + 1}
              </span>

              {/* Jersey avatar with real team colors */}
              <JerseyAvatar
                team={playerTeam}
                number={player.jersey_number}
                size={38}
              />

              {/* Player info */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 dark:text-white truncate">
                  {player.player_name}
                </div>
                {/* Colored team name — no dot */}
                <span
                  className="text-xs font-bold truncate"
                  style={{ color: teamNameColor }}
                >
                  {player.team_name}
                </span>
              </div>

              {/* PPG stat */}
              <div className="text-right flex-shrink-0">
                <span
                  className={`text-sm font-bold tabular-nums ${
                    isFirst
                      ? "text-orange-500"
                      : "text-gray-700 dark:text-gray-200"
                  }`}
                >
                  {player.ppg}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                  PPG
                </span>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
// Weekly Hadith Card
// ─────────────────────────────────────────────────────────────────────────────

const WeeklyHadith = ({ seasonId, seasonStartDate }) => {
  const [hadiths, setHadiths] = useState([]);
  const [viewIndex, setViewIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const currentWeek = (() => {
    if (!seasonStartDate) return 1;
    const start = new Date(seasonStartDate);
    const now = new Date();
    const diff = Math.floor((now - start) / (7 * 24 * 60 * 60 * 1000)) + 1;
    return Math.max(1, diff);
  })();

  useEffect(() => {
    if (!seasonId) return;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("weekly_hadiths")
        .select("week_number, theme, text, reference")
        .eq("season_id", seasonId)
        .eq("is_active", true)
        .order("week_number", { ascending: true });
      const rows = data || [];
      setHadiths(rows);
      if (rows.length > 0) {
        const idx = rows.findIndex((h) => h.week_number === currentWeek);
        setViewIndex(
          idx >= 0 ? idx : Math.min(currentWeek - 1, rows.length - 1),
        );
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 animate-pulse">
        <div className="h-3 w-28 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
        <div className="h-3 w-full bg-gray-100 dark:bg-gray-700/60 rounded mb-2" />
        <div className="h-3 w-5/6 bg-gray-100 dark:bg-gray-700/60 rounded" />
      </div>
    );
  }

  if (!hadiths.length) return null;

  const hadith = hadiths[viewIndex];
  const isCurrentWeek = hadith.week_number === currentWeek;
  const prev = () => setViewIndex((i) => Math.max(0, i - 1));
  const next = () => setViewIndex((i) => Math.min(hadiths.length - 1, i + 1));

  return (
    <div className="bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-900 dark:from-gray-900 dark:via-emerald-950 dark:to-gray-900 rounded-2xl overflow-hidden shadow-sm border border-emerald-800/40 dark:border-emerald-900/60">
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <BookOpen size={14} className="text-emerald-300" />
          <span className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-300">
            Weekly Reminder
          </span>
          {isCurrentWeek && (
            <span className="text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-1.5 py-0.5 rounded-full">
              This Week
            </span>
          )}
        </div>
        {hadiths.length > 1 && (
          <div className="flex items-center gap-1">
            <button
              onClick={prev}
              disabled={viewIndex === 0}
              className="p-1 rounded-md text-emerald-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30"
              aria-label="Previous week"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs text-emerald-400 tabular-nums min-w-[3.5rem] text-center">
              Wk {hadith.week_number}/{hadiths.length}
            </span>
            <button
              onClick={next}
              disabled={viewIndex === hadiths.length - 1}
              className="p-1 rounded-md text-emerald-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30"
              aria-label="Next week"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
      <div className="px-5 py-4">
        {hadith.theme && (
          <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-400 mb-3">
            {hadith.theme}
          </p>
        )}
        <div className="text-4xl leading-none text-emerald-600/40 font-serif select-none mb-1">
          "
        </div>
        <p className="text-white/90 text-sm leading-relaxed font-medium italic">
          {hadith.text}
        </p>
        {hadith.reference && (
          <p className="mt-3 text-xs text-emerald-400/80 font-semibold">
            — Al-Adab Al-Mufrad · {hadith.reference}
          </p>
        )}
      </div>
      <div className="px-5 pb-4">
        <p className="text-[10px] text-emerald-600/60 leading-relaxed">
          Imam Al-Bukhari's Al-Adab Al-Mufrad — character, brotherhood & mercy
          🤲🏽
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Home component
// ─────────────────────────────────────────────────────────────────────────────

const Home = () => {
  const navigate = useNavigate();
  const { activeSeason, sponsors } = useSeason();
  const { data: standingsData, loading: standingsLoading } = useStandings(
    activeSeason?.id,
  );
  const standingsTeams = standingsData.slice(0, 4);

  const [isLive, setIsLive] = useState(false);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [leagueLeaders, setLeagueLeaders] = useState([]);

  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [leadersLoading, setLeadersLoading] = useState(true);

  // Live indicator
  useEffect(() => {
    const checkLive = async () => {
      const { data } = await supabase
        .from("games")
        .select("id")
        .eq("status", "live")
        .limit(1);
      setIsLive(data && data.length > 0);
    };
    checkLive();
    const channel = supabase
      .channel("home_live_indicator")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "games" },
        checkLive,
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  // Upcoming games — include team color fields
  // Filter strictly to games whose game_date is in the future relative to the
  // current UTC moment.  Using toISOString() gives a timezone-unambiguous
  // ISO-8601 string that Postgres can compare directly against a timestamptz
  // column, avoiding any local-timezone edge cases.
  const fetchUpcoming = useCallback(async () => {
    if (!activeSeason) return;
    setUpcomingLoading(true);
    const isPlayoffs = activeSeason.status === "playoffs";
    const nowIso = new Date().toISOString(); // e.g. "2026-09-23T23:33:47.549Z"
    const { data, error } = await supabase
      .from("games")
      .select(
        `id, game_date, court, game_type, playoff_round,
         team1:teams!games_team1_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient),
         team2:teams!games_team2_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient)`,
      )
      .eq("season_id", activeSeason.id)
      .in("game_type", isPlayoffs ? ["playoff"] : ["regular"])
      .eq("status", "scheduled")
      .gt("game_date", nowIso) // ← only games strictly after right now
      .order("game_date", { ascending: true })
      .limit(4);
    if (error) console.error("Upcoming games error:", error);
    else setUpcomingGames(data || []);
    setUpcomingLoading(false);
  }, [activeSeason]);

  // Recent results — include team color fields
  const fetchRecent = useCallback(async () => {
    if (!activeSeason) return;
    setResultsLoading(true);
    const { data, error } = await supabase
      .from("games")
      .select(
        `id, game_date, team1_score, team2_score,
         team1:teams!games_team1_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient),
         team2:teams!games_team2_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient)`,
      )
      .eq("season_id", activeSeason.id)
      .eq("status", "finished")
      .in("game_type", ["regular", "playoff"])
      .order("game_date", { ascending: false })
      .limit(3);
    if (error) console.error("Recent results error:", error);
    else setRecentResults(data || []);
    setResultsLoading(false);
  }, [activeSeason]);

  // League leaders — two-step fetch so season/status/type filters are reliable
  // Step 1: get finished regular-season game IDs for the active season
  // Step 2: fetch game_stats for those game IDs with full player+team color data
  const fetchLeaders = useCallback(async () => {
    if (!activeSeason) return;
    setLeadersLoading(true);

    // Step 1: resolve valid game IDs
    const { data: games, error: gamesError } = await supabase
      .from("games")
      .select("id")
      .eq("season_id", activeSeason.id)
      .eq("status", "finished")
      .eq("game_type", "regular");

    if (gamesError || !games?.length) {
      setLeagueLeaders([]);
      setLeadersLoading(false);
      return;
    }

    const gameIds = games.map((g) => g.id);

    // Step 2: fetch stats for those games
    const { data, error } = await supabase
      .from("game_stats")
      .select(
        `player_id, points,
         game_id,
         player:players!game_stats_player_id_fkey(
           id, name, jersey_number,
           team:teams!players_team_id_fkey(id, name, logo_url, primary_color, secondary_color, gradient)
         )`,
      )
      .in("game_id", gameIds);

    if (error) {
      console.error("League leaders error:", error);
      setLeagueLeaders([]);
      setLeadersLoading(false);
      return;
    }

    // Aggregate per player
    const map = {};
    for (const row of data ?? []) {
      if (!row.player) continue;
      const pid = row.player_id;
      if (!map[pid]) {
        map[pid] = {
          player_id: pid,
          player_name: row.player.name,
          jersey_number: row.player.jersey_number,
          team_name: row.player.team?.name ?? "—",
          logo_url: row.player.team?.logo_url ?? null,
          primary_color: row.player.team?.primary_color ?? null,
          secondary_color: row.player.team?.secondary_color ?? null,
          gradient: row.player.team?.gradient ?? false,
          games: new Set(),
          total_pts: 0,
        };
      }
      map[pid].games.add(row.game_id);
      map[pid].total_pts += row.points ?? 0;
    }

    const result = Object.values(map)
      .map((p) => {
        const gp = p.games.size || 1;
        return { ...p, gp, ppg: (p.total_pts / gp).toFixed(1) };
      })
      .sort((a, b) => parseFloat(b.ppg) - parseFloat(a.ppg))
      .slice(0, 5);

    setLeagueLeaders(result);
    setLeadersLoading(false);
  }, [activeSeason]);

  useEffect(() => {
    fetchUpcoming();
    fetchRecent();
    fetchLeaders();
  }, [fetchUpcoming, fetchRecent, fetchLeaders]);

  const topPlayer = leagueLeaders[0] ?? null;

  return (
    <div className="space-y-5">
      {/* Live banner */}
      {isLive && <LiveBanner onNavigate={() => navigate("/live")} />}

      {/* Hero with photo carousel */}
      <Hero
        activeSeason={activeSeason}
        onStandings={() => navigate("/standings")}
        onStats={() => navigate("/stats")}
      />

      {/* Quick-access: 2 cards with dynamic team colors */}
      <QuickLinks
        standingsTop={standingsTeams[0] ?? null}
        topPlayer={topPlayer}
        onNavigate={navigate}
      />

      {/* Weekly Hadith */}
      {activeSeason && (
        <WeeklyHadith
          seasonId={activeSeason.id}
          seasonStartDate={activeSeason.start_date}
        />
      )}

      {/* Main grid */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-5">
          <RecentResults
            games={recentResults}
            loading={resultsLoading}
            onGameClick={(id) => navigate(`/game/${id}`)}
            onSeeAll={() => navigate("/schedule")}
          />
          <StandingsSnapshot
            teams={standingsTeams}
            loading={standingsLoading}
            onTeamClick={(id) => navigate(`/teams/${id}`)}
            onSeeAll={() => navigate("/standings")}
          />
        </div>
        <div className="space-y-5">
          <LeagueLeaders
            leaders={leagueLeaders}
            loading={leadersLoading}
            onSeeAll={() => navigate("/stats")}
          />
          <UpcomingGames
            games={upcomingGames}
            loading={upcomingLoading}
            onSeeAll={() => navigate("/schedule")}
          />
        </div>
      </div>

      {/* Sponsors */}
      {sponsors.length > 0 && (
        <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          <div className="px-6 pt-6 pb-4 flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-600 to-transparent" />
            <span className="text-[11px] font-black uppercase tracking-[0.25em] text-gray-400 dark:text-gray-500 whitespace-nowrap">
              Proudly Sponsored By
            </span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-600 to-transparent" />
          </div>
          <div className="px-6 pb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {sponsors.map((s) => (
              <SponsorCard key={s.id} sponsor={s} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
